import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { withStyles, type AccountUserRole } from 'vopay';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import styles from './EditRoleDrawer.module.scss';

const cn = withStyles(styles);

type RoleChange = {
  role: AccountUserRole;
  newName: string;
  hasNameChanged: boolean;
};

type SubAccountRoleChange = RoleChange & { numSubAccounts: number };

// The confirmation dialogs an edit can need, one per way the "apply to all sub-accounts" flag and the
// account's sub-accounts interact with the change. Each resolves true when the admin confirms.
export default function useEditRoleConfirmations() {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.EditRoleDrawer' });
  const { t: tButtons } = useTranslation('translations', { keyPrefix: 'Shared.Buttons' });
  const alertDialog = useAlertDialog();

  async function confirm(title: string, content: ReactNode) {
    const result = await alertDialog.caution(title, content, {
      abortLabel: tButtons('cancel', { defaultValue: 'Cancel' }),
      confirmLabel: tButtons('confirm', { defaultValue: 'Confirm' }),
    });

    return result === 'confirm';
  }

  // Old and new name when it changed, otherwise just the role's name.
  function renderRoleCard({ role, newName, hasNameChanged }: RoleChange) {
    if (!hasNameChanged) {
      return (
        <div className={cn('confirmDialogCard')}>
          <div className={cn('row', 'rowCentered')}>
            <span className={cn('label')}>{t('confirmRoleLabel', { defaultValue: 'Role:' })}</span>
            <span className={cn('value')}>{newName}</span>
          </div>
        </div>
      );
    }

    return (
      <div className={cn('confirmDialogCard')}>
        <div className={cn('row')}>
          <span className={cn('label')}>{t('confirmOldName', { defaultValue: 'Old Name' })}</span>
          <span className={cn('value')}>{role.Name}</span>
        </div>

        <div className={cn('row', 'rowDivider')}>
          <span className={cn('label')}>{t('confirmNewName', { defaultValue: 'New Name' })}</span>
          <span className={cn('value')}>{newName}</span>
        </div>
      </div>
    );
  }

  // The flag stayed off on an account with sub-accounts: the edit only touches this account's role.
  function confirmLocalOnlyEdit(change: RoleChange) {
    return confirm(
      change.hasNameChanged
        ? t('confirmEditNameTitle', { defaultValue: "Do you want to update this role's name?" })
        : t('confirmEditPermsTitle', { defaultValue: "Do you want to update this role's permissions?" }),
      <div data-apply-confirm-dialog="off-off-edit">
        {renderRoleCard(change)}

        <p className={cn('confirmDialogLead')}>
          {t('confirmEditLocalOnly', {
            defaultValue: 'Saving these changes will update the role on this account only.',
          })}
        </p>

        <p className={cn('confirmDialogLead')}>
          {t('confirmEditNoSubaccounts', { defaultValue: "Sub-accounts aren't affected." })}
        </p>
      </div>,
    );
  }

  // On → Off: the sub-account copies stay in place as standalone roles, but the parent stops cascading.
  function confirmStopApplying(change: SubAccountRoleChange) {
    return confirm(
      t('confirmStopTitle', { defaultValue: 'Do you want to stop applying this role to sub-accounts?' }),
      <div data-apply-confirm-dialog="on-to-off">
        {renderRoleCard(change)}

        {change.hasNameChanged && (
          <p className={cn('confirmDialogHint')}>
            {t('confirmStopRenameHint', { defaultValue: 'Role name change applies to only this account.' })}
          </p>
        )}

        <p className={cn('confirmDialogLead')}>{t('confirmStopLead', { defaultValue: 'Saving this change will:' })}</p>

        <ul className={cn('confirmDialogList')}>
          <li>
            {t('confirmStopBullet1', {
              defaultValue:
                'Keep the role applied on {{numSubAccounts}} sub-account where it was previously applied as a standalone role no longer linked to this parent.',
              numSubAccounts: change.numSubAccounts,
            })}
          </li>

          <li>
            {t('confirmStopBullet2', {
              defaultValue: 'Stop any current or future updates to this role cascading to sub-accounts.',
            })}
          </li>

          <li>{t('confirmStopBullet3', { defaultValue: 'Prevent new sub-accounts from inheriting this role.' })}</li>

          <li>
            {t('confirmStopBullet4', {
              defaultValue: 'Leave all current user assignments on sub-accounts unchanged.',
            })}
          </li>
        </ul>

        <p className={cn('confirmDialogLead')}>
          {t('confirmStopFooter', {
            defaultValue: 'Sub-accounts will be able to edit their copy of the role independently from now on.',
          })}
        </p>
      </div>,
    );
  }

  // The flag was already on: the change fans out to every sub-account.
  function confirmUpdateAcrossSubAccounts(change: SubAccountRoleChange) {
    const appliedTo = t('confirmAppliedToCount', {
      defaultValue: 'This role is applied to {{numSubAccounts}} sub-accounts.',
      numSubAccounts: change.numSubAccounts,
    });

    return confirm(
      t('confirmUpdateTitle', { defaultValue: 'Do you want to update role on this account and sub-accounts?' }),
      change.hasNameChanged ? (
        <div data-apply-confirm-dialog="rename-update">
          {renderRoleCard(change)}
          <p className={cn('confirmDialogLead')}>{appliedTo}</p>

          <ul className={cn('confirmDialogList')}>
            <li>
              {t('confirmUpdateBullet1', {
                defaultValue:
                  'If there is any existing "{{roleName}}" role it will be replaced with the new role name and permissions will be updated (if applicable).',
                roleName: change.role.Name,
              })}
            </li>

            <li>
              {t('confirmUpdateBullet2', {
                defaultValue: 'Updated role will be applied to all the existing and any future sub-accounts.',
              })}
            </li>
          </ul>
        </div>
      ) : (
        <div data-apply-confirm-dialog="perms-update">
          {renderRoleCard(change)}

          <p className={cn('confirmDialogLead')}>
            {appliedTo}
            <br />
            {t('confirmEditLeadShared', { defaultValue: 'Saving these changes will:' })}
          </p>

          <ul className={cn('confirmDialogList')}>
            <li>
              {t('confirmPermsUpdateBullet1', {
                defaultValue: 'Apply this role to all {{numSubAccounts}} sub-accounts and any new sub-account.',
                numSubAccounts: change.numSubAccounts,
              })}
            </li>

            <li>
              {t('confirmPermsUpdateBullet2', {
                defaultValue:
                  'If there is any existing role with the same name, it will be updated with the new permissions.',
              })}
            </li>
          </ul>
        </div>
      ),
    );
  }

  // Off → On is destructive (it creates the role in every sub-account), so it is confirmed first.
  function confirmApplyToAll(change: RoleChange) {
    return confirm(
      t('confirmApplyTitle', { defaultValue: 'Do you want to apply this role to all sub-accounts?' }),
      change.hasNameChanged ? (
        <div data-apply-confirm-dialog="rename">
          {renderRoleCard(change)}

          <ul className={cn('confirmDialogList')}>
            <li>
              {t('confirmRenameBullet1', {
                defaultValue:
                  'If there is any existing "{{roleName}}" role it will be replaced with the new role name and permissions will be updated (if applicable).',
                roleName: change.role.Name,
              })}
            </li>

            <li>
              {t('confirmRenameBullet2', {
                defaultValue: 'New role will be applied to all the existing and any future sub-accounts.',
              })}
            </li>
          </ul>
        </div>
      ) : (
        <div data-apply-confirm-dialog="keep-name">
          {renderRoleCard(change)}

          <ul className={cn('confirmDialogList')}>
            <li>
              {t('confirmKeepNameBullet1', {
                defaultValue: 'Apply this role to all existing sub-accounts and any new sub-account.',
              })}
            </li>

            <li>
              {t('confirmKeepNameBullet2', {
                defaultValue:
                  'If there is any existing role with the same name, it will be updated with the new permissions.',
              })}
            </li>
          </ul>
        </div>
      ),
    );
  }

  return { confirmLocalOnlyEdit, confirmStopApplying, confirmUpdateAcrossSubAccounts, confirmApplyToAll };
}
